import { useCallback, useEffect, useState } from "react";
import { Banknote, CheckCircle2, Hand, ShieldAlert, TriangleAlert } from "lucide-react";

import { ApiError, api, apiGet } from "../api";
import {
  type CashCollectionReceipt,
  type CashHandoverReceipt,
  type CashInvariant,
  type CashPosition,
  type CashWorkspace,
  compareMinor,
  decimalToMinor,
  formatCash,
  mayRotateIdempotencyKey,
  minorToDecimal,
  newIdempotencyKey,
  partyLabel,
} from "../cash";
import { projectionAsOf } from "../money";

function postJson<T>(path: string, body: unknown, idempotencyKey?: string) {
  const headers: Record<string, string> = {};
  if (idempotencyKey) headers["Idempotency-Key"] = idempotencyKey;
  return api<T>(path, { method: "POST", body: JSON.stringify(body), headers });
}

function errorText(e: unknown, fallback: string): string {
  if (e instanceof ApiError && e.status < 500) return `${fallback}: ${e.message}`;
  return `${fallback}: the outcome is unknown (network). Refresh before retrying — a retry reuses the same key.`;
}

function AcknowledgeRow({ h, onDone }: { h: CashHandoverReceipt; onDone: () => void }) {
  const [amount, setAmount] = useState(minorToDecimal(h.amount_minor, h.currency_exponent) ?? "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const received = decimalToMinor(amount, h.currency_exponent);
  const short = received !== null && compareMinor(received, h.amount_minor) < 0;
  const over = received !== null && compareMinor(received, h.amount_minor) > 0;

  const ack = async () => {
    if (received === null || over) return;
    setBusy(true);
    setErr(null);
    try {
      await postJson(`/cash/handovers/${h.id}/acknowledge`, { received_minor: received, note: note || null });
      onDone();
    } catch (e) {
      setErr(errorText(e, "Acknowledgement not recorded"));
    } finally {
      setBusy(false);
    }
  };
  const decline = async () => {
    setBusy(true);
    setErr(null);
    try {
      await postJson(`/cash/handovers/${h.id}/decline`, { note: note || null });
      onDone();
    } catch (e) {
      setErr(errorText(e, "Decline not recorded"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cash-row">
      <div className="cash-main">
        <span className="cash-title">
          {partyLabel(h.from)} declares {formatCash(h.amount_minor, h.currency_exponent, h.currency)}
        </span>
        <span className="cash-sub">
          Requested {projectionAsOf(h.requested_at)} · the cash stays with the rider until you acknowledge it
          {h.note ? ` · “${h.note}”` : ""}
        </span>
        <div className="cash-form">
          <label>
            Counted ({h.currency})
            <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" />
          </label>
          <label>
            Note{short ? " (required: counted less)" : ""}
            <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
          </label>
          <button className="primary" disabled={busy || received === null || over || (short && !note.trim())}
            onClick={() => void ack()}>
            {short ? "Acknowledge counted (dispute)" : "Acknowledge received"}
          </button>
          <button className="courier-btn" disabled={busy} onClick={() => void decline()}>Decline</button>
        </div>
        {received === null && <span className="err">Enter the counted amount with at most {h.currency_exponent} decimals.</span>}
        {over && <span className="err">You cannot acknowledge more than the rider declared.</span>}
        {err && <span className="err">{err}</span>}
      </div>
    </div>
  );
}

function RemitRow({ p, onDone }: { p: CashPosition; onDone: () => void }) {
  const [amount, setAmount] = useState(minorToDecimal(p.available_minor, p.currency_exponent) ?? "");
  const [reference, setReference] = useState("");
  const [key, setKey] = useState(() => newIdempotencyKey("remit"));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const minor = decimalToMinor(amount, p.currency_exponent);
  const tooMuch = minor !== null && compareMinor(minor, p.available_minor) > 0;

  const remit = async () => {
    if (minor === null || tooMuch || !reference.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await postJson("/cash/remittances", {
        currency: p.currency, beneficiary_kind: p.beneficiary.kind, beneficiary_id: p.beneficiary.id,
        amount_minor: minor, reference: reference.trim(),
      }, key);
      setKey(newIdempotencyKey("remit"));
      setReference("");
      onDone();
    } catch (e) {
      // Only a definitive refusal (4xx) gets a fresh key; a 5xx / gateway timeout / network error is an unknown
      // outcome (the server may have committed), so the key is kept and a retry cannot double-remit (MC-28).
      if (mayRotateIdempotencyKey(e)) setKey(newIdempotencyKey("remit"));
      setErr(errorText(e, "Remittance not recorded"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cash-row">
      <div className="cash-main">
        <span className="cash-title">
          {formatCash(p.outstanding_minor, p.currency_exponent, p.currency)} held for {partyLabel(p.beneficiary)}
        </span>
        <span className="cash-sub">Available to remit {formatCash(p.available_minor, p.currency_exponent, p.currency)}</span>
        <div className="cash-form">
          <label>
            Amount ({p.currency})
            <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" />
          </label>
          <label>
            Deposit / payout reference
            <input value={reference} onChange={(e) => setReference(e.target.value)} maxLength={120} />
          </label>
          <button className="primary" disabled={busy || minor === null || minor === "0" || tooMuch || !reference.trim()}
            onClick={() => void remit()}>
            Record remittance
          </button>
        </div>
        {tooMuch && <span className="err">More than you hold for this beneficiary.</span>}
        {err && <span className="err">{err}</span>}
      </div>
    </div>
  );
}

function HandToRow({ p, receivers, onDone }: {
  p: CashPosition; receivers: { id: string; name: string; role: string }[]; onDone: () => void;
}) {
  const [amount, setAmount] = useState(minorToDecimal(p.available_minor, p.currency_exponent) ?? "");
  const [receiver, setReceiver] = useState(receivers.find((r) => r.role !== "dispatcher")?.id ?? receivers[0]?.id ?? "");
  const [key, setKey] = useState(() => newIdempotencyKey("hand"));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const minor = decimalToMinor(amount, p.currency_exponent);
  const tooMuch = minor !== null && compareMinor(minor, p.available_minor) > 0;
  const send = async () => {
    if (minor === null || minor === "0" || tooMuch || !receiver) return;
    setBusy(true);
    setErr(null);
    try {
      await postJson("/cash/handovers", {
        receiver_id: receiver, currency: p.currency, beneficiary_kind: p.beneficiary.kind,
        beneficiary_id: p.beneficiary.id, amount_minor: minor,
      }, key);
      setKey(newIdempotencyKey("hand"));
      onDone();
    } catch (e) {
      if (mayRotateIdempotencyKey(e)) setKey(newIdempotencyKey("hand"));
      setErr(errorText(e, "Handover not recorded"));
    } finally {
      setBusy(false);
    }
  };
  if (receivers.length === 0) return null;
  return (
    <div className="cash-form">
      <label>
        Hand to
        <select value={receiver} onChange={(e) => setReceiver(e.target.value)}>
          {receivers.map((r) => <option key={r.id} value={r.id}>{r.name} ({r.role})</option>)}
        </select>
      </label>
      <label>
        Amount ({p.currency})
        <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" />
      </label>
      <button className="courier-btn" disabled={busy || minor === null || minor === "0" || tooMuch || !receiver}
        onClick={() => void send()}>
        Hand over (they must acknowledge)
      </button>
      {tooMuch && <span className="err">More than you hold unreserved for this beneficiary.</span>}
      {err && <span className="err">{err}</span>}
    </div>
  );
}

function ReassignRow({ p, onDone }: { p: CashPosition; onDone: () => void }) {
  const [amount, setAmount] = useState(minorToDecimal(p.available_minor, p.currency_exponent) ?? "");
  const [note, setNote] = useState("");
  const [key, setKey] = useState(() => newIdempotencyKey("reassign"));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const minor = decimalToMinor(amount, p.currency_exponent);
  const over = minor !== null && compareMinor(minor, p.available_minor) > 0;
  const short = minor !== null && compareMinor(minor, p.available_minor) < 0;
  const go = async () => {
    if (minor === null || over || !note.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await postJson("/cash/holders/reassign", {
        holder_kind: p.holder.kind, holder_id: p.holder.id, currency: p.currency,
        beneficiary_kind: p.beneficiary.kind, beneficiary_id: p.beneficiary.id,
        received_minor: minor, note: note.trim(),
      }, key);
      setKey(newIdempotencyKey("reassign"));
      onDone();
    } catch (e) {
      if (mayRotateIdempotencyKey(e)) setKey(newIdempotencyKey("reassign"));
      setErr(errorText(e, "Reassignment not recorded"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="cash-row">
      <div className="cash-main">
        <span className="cash-title">
          {partyLabel(p.holder)} (no longer active) holds {formatCash(p.available_minor, p.currency_exponent, p.currency)} for {partyLabel(p.beneficiary)}
        </span>
        <span className="cash-sub">Take it into your custody with what you physically recovered; any shortfall stays recorded against them.</span>
        <div className="cash-form">
          <label>
            Recovered ({p.currency})
            <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" />
          </label>
          <label>
            Note (required)
            <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
          </label>
          <button className="primary" disabled={busy || minor === null || over || !note.trim()} onClick={() => void go()}>
            {short ? "Take recovered amount (dispute the rest)" : "Take into my custody"}
          </button>
        </div>
        {over && <span className="err">More than they hold unreserved.</span>}
        {err && <span className="err">{err}</span>}
      </div>
    </div>
  );
}

function SettleReceivable({ c, onDone }: { c: CashCollectionReceipt; onDone: () => void }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const settle = async (outcome: "recovered" | "written_off") => {
    setBusy(true);
    setErr(null);
    try {
      await postJson(`/cash/receivables/${c.id}/settle`, { outcome, note: note.trim() });
      onDone();
    } catch (e) {
      setErr(errorText(e, "Receivable not settled"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="cash-form">
      <label>
        Settlement note
        <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
      </label>
      <button className="courier-btn" disabled={busy || !note.trim()} onClick={() => void settle("recovered")}>Customer paid the rest</button>
      <button className="courier-btn" disabled={busy || !note.trim()} onClick={() => void settle("written_off")}>Write off</button>
      {err && <span className="err">{err}</span>}
    </div>
  );
}

function DisputeRow({ kind, id, title, sub, canResolve, onDone }: {
  kind: "collection" | "handover"; id: string; title: string; sub: string; canResolve: boolean; onDone: () => void;
}) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const resolve = async () => {
    setBusy(true);
    setErr(null);
    try {
      await postJson(`/cash/disputes/${kind}/${id}/resolve`, { note: note.trim() });
      onDone();
    } catch (e) {
      setErr(errorText(e, "Resolution not recorded"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="cash-row">
      <div className="cash-main">
        <span className="cash-title">{title}</span>
        <span className="cash-sub">{sub}</span>
        {canResolve && (
          <div className="cash-form">
            <label>
              Resolution note (does not move cash)
              <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
            </label>
            <button className="courier-btn" disabled={busy || !note.trim()} onClick={() => void resolve()}>Resolve</button>
          </div>
        )}
        {err && <span className="err">{err}</span>}
      </div>
    </div>
  );
}

function collectionLine(c: CashCollectionReceipt): string {
  return `${c.holder_name || c.holder.id.slice(0, 8)} collected ${formatCash(c.amount_minor, c.currency_exponent, c.currency)} `
    + `of ${formatCash(c.due_minor, c.currency_exponent, c.currency)} · receivable `
    + `${formatCash(c.receivable_minor, c.currency_exponent, c.currency)}`;
}

export default function Cash() {
  const [data, setData] = useState<CashWorkspace | null>(null);
  const [invariant, setInvariant] = useState<CashInvariant | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [switching, setSwitching] = useState(false);

  const loadWorkspace = useCallback(async () => {
    try {
      setData(await apiGet<CashWorkspace>("/cash/workspace"));
      setErr(null);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Failed to load the cash workspace");
    }
  }, []);
  // The full invariant re-derivation is its own read: on open and after this operator's writes, not on every poll.
  const loadInvariant = useCallback(async () => {
    try {
      setInvariant((await apiGet<{ invariant: CashInvariant }>("/cash/invariant")).invariant);
    } catch {
      setInvariant(null);
    }
  }, []);
  const load = useCallback(async () => {
    await Promise.all([loadWorkspace(), loadInvariant()]);
  }, [loadWorkspace, loadInvariant]);

  useEffect(() => {
    void load();
    const t = setInterval(() => void loadWorkspace(), 20000);
    return () => clearInterval(t);
  }, [load, loadWorkspace]);

  const valid = data?.cash_custody?.contract_version === "cash-custody.v1" ? data : null;
  const status = valid?.cash_custody;
  const me = valid?.me;

  const toggle = async (enabled: boolean) => {
    setSwitching(true);
    try {
      await postJson("/cash/settings", { enabled });
      await load();
    } catch (e) {
      setErr(errorText(e, "Setting not saved"));
    } finally {
      setSwitching(false);
    }
  };

  const mine = valid?.pending_handovers.filter((h) => h.to_me) ?? [];
  const others = valid?.pending_handovers.filter((h) => !h.to_me) ?? [];
  const receivers = valid?.receivers ?? [];
  const cancelMine = async (id: string) => {
    try {
      await postJson(`/cash/handovers/${id}/cancel`, {});
      await load();
    } catch (e) {
      setErr(errorText(e, "Cancel not recorded"));
    }
  };

  return (
    <div className="board-page cash-page">
      <div className="board-header">
        <h1>Cash custody</h1>
        {status && <span className={`chip cash-mode cash-mode-${status.mode}`}>{status.mode}</span>}
        <div className="spacer" />
        {me?.can_move_money && status && (
          <button className="courier-btn" disabled={switching} onClick={() => void toggle(!status.enabled)}>
            {status.enabled ? "Switch cash custody off" : "Switch cash custody on"}
          </button>
        )}
      </div>
      {err && <div className="banner-err">{err}{data ? " — showing the last successful read, which may be stale." : ""}</div>}
      {status?.mode === "draining" && (
        <div className="banner-err">
          Cash custody is switched off with cash still in custody. No new collections are recorded; the backlog
          below can still be handed over, acknowledged, remitted and resolved. Switching back on resumes collection.
        </div>
      )}
      {status?.mode === "off" && (
        <p className="cap-note">Cash custody is off for this shop: riders cannot record cash on delivery.
          Cash-on-delivery stops still complete, but their door cash is marked untracked (audited) until it is switched on.</p>
      )}
      {invariant && !invariant.ok && (
        <div className="banner-err">
          Custody invariant broken on {invariant.breaks.length} position(s):{" "}
          {invariant.breaks.map((b) => `${partyLabel(b.holder)} ${b.currency} ${b.reason}`).join("; ")}
        </div>
      )}
      {valid && <p className="cap-note">{projectionAsOf(valid.as_of)} · a returned parcel never generates a cash
        refund · invariant {invariant === null ? "unknown (check unavailable)" : invariant.ok ? "holds" : "BROKEN"}
        {invariant ? ` over ${invariant.checked_positions} position(s)` : ""}</p>}

      <div className="kpis cash-kpis">
        <div className="kpi">
          <div className="kpi-icon"><Banknote size={18} /></div>
          <div className="kpi-body">
            <div className="kpi-value cash-kpi-value">
              {!valid ? "—" : valid.by_currency.length === 0 ? "None"
                : valid.by_currency.map((t) => <div key={t.currency}>{formatCash(t.outstanding_minor, t.currency_exponent, t.currency)}</div>)}
            </div>
            <div className="kpi-label">Cash in custody, by currency</div>
          </div>
        </div>
        <div className="kpi kpi-attention">
          <div className="kpi-icon"><Hand size={18} /></div>
          <div className="kpi-body">
            <div className="kpi-value cash-kpi-value">
              {!valid ? "—" : valid.by_currency.filter((t) => t.reserved_minor !== "0").length === 0 ? "None"
                : valid.by_currency.filter((t) => t.reserved_minor !== "0")
                  .map((t) => <div key={t.currency}>{formatCash(t.reserved_minor, t.currency_exponent, t.currency)}</div>)}
            </div>
            <div className="kpi-label">Declared in handovers awaiting acknowledgement</div>
          </div>
        </div>
        <div className="kpi kpi-risk">
          <div className="kpi-icon"><TriangleAlert size={18} /></div>
          <div className="kpi-body">
            <div className="kpi-value cash-kpi-value">
              {!valid ? "—" : valid.receivables.by_currency.length === 0 ? "None"
                : valid.receivables.by_currency.map((r) => <div key={r.currency}>{formatCash(r.receivable_minor, r.currency_exponent, r.currency)} · {r.stops} stop(s)</div>)}
            </div>
            <div className="kpi-label">Customer receivables (partial / disputed collections)</div>
          </div>
        </div>
      </div>

      <section className="lane cash-section">
        <header className="lane-head"><span>Awaiting your acknowledgement</span><span className="lane-count">{mine.length}</span></header>
        <div className="lane-body">
          {!valid ? <p className="lane-empty">{err ? "Unavailable" : "Loading…"}</p>
            : mine.length === 0 ? <p className="lane-empty">No handovers are addressed to you</p>
              : mine.map((h) => <AcknowledgeRow key={h.id} h={h} onDone={() => void load()} />)}
        </div>
      </section>

      {others.length > 0 && (
        <section className="lane cash-section">
          <header className="lane-head"><span>Other pending handovers</span><span className="lane-count">{others.length}</span></header>
          <div className="lane-body">
            {others.map((h) => (
              <div className="cash-row" key={h.id}>
                <div className="cash-main">
                  <span className="cash-title">{partyLabel(h.from)} → {partyLabel(h.to)}: {formatCash(h.amount_minor, h.currency_exponent, h.currency)}</span>
                  <span className="cash-sub">Cash stays with the giver until {h.to.name || "the receiver"} acknowledges</span>
                  {h.from_me && <div className="cash-form"><button className="courier-btn" onClick={() => void cancelMine(h.id)}>Cancel my handover</button></div>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="lane cash-section">
        <header className="lane-head"><span>Outstanding by holder</span><span className="lane-count">{valid?.holders.length ?? "—"}</span></header>
        <div className="lane-body cash-table-wrap">
          <table className="cash-table">
            <thead><tr><th>Holder</th><th>For</th><th>Currency</th><th>Outstanding</th><th>Awaiting ack</th></tr></thead>
            <tbody>
              {(valid?.holders ?? []).map((p) => (
                <tr key={`${p.holder.kind}-${p.holder.id}-${p.currency}-${p.beneficiary.id}`}>
                  <td>{partyLabel(p.holder)}</td>
                  <td>{partyLabel(p.beneficiary)}</td>
                  <td>{p.currency}</td>
                  <td className="mono">{minorToDecimal(p.outstanding_minor, p.currency_exponent) ?? "unknown"}</td>
                  <td className="mono">{minorToDecimal(p.reserved_minor, p.currency_exponent) ?? "unknown"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {valid && valid.holders.length === 0 && <p className="lane-empty">Nobody holds custody cash</p>}
        </div>
      </section>

      <section className="lane cash-section">
        <header className="lane-head"><span>Outstanding by day (oldest cash first)</span><span className="lane-count">{valid?.by_day.length ?? "—"}</span></header>
        <div className="lane-body cash-table-wrap">
          <table className="cash-table">
            <thead><tr><th>Day ({valid?.day_basis ?? "UTC"})</th><th>Holder</th><th>Currency</th><th>Still held</th></tr></thead>
            <tbody>
              {(valid?.by_day ?? []).map((r) => (
                <tr key={`${r.holder.id}-${r.currency}-${r.day}`}>
                  <td className="mono">{r.day}</td>
                  <td>{partyLabel(r.holder)}</td>
                  <td>{r.currency}</td>
                  <td className="mono">{minorToDecimal(r.outstanding_minor, r.currency_exponent) ?? "unknown"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {valid && !valid.aging_complete && <p className="cap-note">Aging is partial (history beyond the read limit).</p>}
          {valid?.coverage.positions_truncated && <p className="cap-note">Only the first {valid.coverage.positions_limit} positions are listed; the currency totals above cover all of them.</p>}
        </div>
      </section>

      {valid && (me?.can_move_money || valid.my_holdings.length > 0) && (
        <section className="lane cash-section">
          <header className="lane-head"><span>Your custody — {me?.can_move_money ? "remit or hand over" : "hand over to someone who can remit"}</span><span className="lane-count">{valid.my_holdings.length}</span></header>
          <div className="lane-body">
            {valid.my_holdings.length === 0 ? <p className="lane-empty">You hold no custody cash</p>
              : valid.my_holdings.map((p) => (
                <div key={`${p.currency}-${p.beneficiary.id}`}>
                  {me?.can_move_money ? <RemitRow p={p} onDone={() => void load()} /> : (
                    <div className="cash-row"><div className="cash-main"><span className="cash-title">
                      {formatCash(p.outstanding_minor, p.currency_exponent, p.currency)} held for {partyLabel(p.beneficiary)}
                    </span></div></div>
                  )}
                  <HandToRow p={p} receivers={receivers} onDone={() => void load()} />
                </div>
              ))}
          </div>
        </section>
      )}

      {me?.can_move_money && (valid?.departed_holdings ?? []).length > 0 && (
        <section className="lane cash-section">
          <header className="lane-head"><span>Cash held by people who left</span><span className="lane-count">{valid!.departed_holdings!.length}</span></header>
          <div className="lane-body">
            {valid!.departed_holdings!.map((p) => (
              <ReassignRow key={`${p.holder.id}-${p.currency}-${p.beneficiary.id}`} p={p} onDone={() => void load()} />
            ))}
          </div>
        </section>
      )}

      <section className="lane cash-section">
        <header className="lane-head">
          <span><ShieldAlert size={13} /> Disputes</span>
          <span className="lane-count">{valid ? valid.disputes.collections.length + valid.disputes.handovers.length : "—"}</span>
        </header>
        <div className="lane-body">
          {valid && valid.disputes.collections.length + valid.disputes.handovers.length === 0 && (
            <p className="lane-empty"><CheckCircle2 size={12} /> No open disputes</p>
          )}
          {valid?.disputes.collections.map((c) => (
            <DisputeRow key={c.id} kind="collection" id={c.id} canResolve={!!me?.can_move_money} onDone={() => void load()}
              title={`Door dispute: ${collectionLine(c)}`} sub={`Reason: ${c.dispute?.reason ?? "—"}`} />
          ))}
          {valid?.disputes.handovers.map((h) => (
            <DisputeRow key={h.id} kind="handover" id={h.id} canResolve={!!me?.can_move_money} onDone={() => void load()}
              title={`Handover dispute: ${partyLabel(h.from)} declared ${formatCash(h.amount_minor, h.currency_exponent, h.currency)}, `
                + `${partyLabel(h.to)} counted ${formatCash(h.received_minor, h.currency_exponent, h.currency)}`}
              sub={`The difference stays with the rider. ${h.response_note ?? ""}`} />
          ))}
        </div>
      </section>

      <section className="lane cash-section">
        <header className="lane-head"><span>Receivables by stop</span><span className="lane-count">{valid?.receivables.stops.length ?? "—"}</span></header>
        <div className="lane-body">
          {(valid?.receivables.stops ?? []).length === 0 ? <p className="lane-empty">No open customer receivables</p>
            : valid!.receivables.stops.map((c) => (
              <div className="cash-row" key={c.id}>
                <div className="cash-main">
                  <span className="cash-title">{collectionLine(c)}</span>
                  <span className="cash-sub">{c.state} · {projectionAsOf(c.collected_at)}
                    {c.shortfall_reason ? ` · rider: “${c.shortfall_reason}”` : ""}{c.dispute?.reason ? ` · dispute: “${c.dispute.reason}”` : ""}</span>
                  {me?.can_move_money && <SettleReceivable c={c} onDone={() => void load()} />}
                </div>
              </div>
            ))}
        </div>
      </section>
    </div>
  );
}
